import AppAppearance from "./AppAppearance";
import { applyTheme, readThemePreference } from "./appTheme";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";

// Both screens are loaded on demand, so a tenant opening their JMF Estates
// portal downloads the portal and NOT the Nexus application — its code, its
// seed data and everything compiled into it stay out of their browser.
const App = React.lazy(() => import("./App"));
const EstatesPortal = React.lazy(() => import("./estates/EstatesPortal"));

// The tenant portal: #/estates, or a property's own word as the path —
// jmfestates.com/roytec — with the tenant's key after the hash. Decided here,
// ahead of App, because App's first act is to start a Supabase session and a
// tenant has none. Nexus itself only ever lives at "/".
// On the portal's own domain, everything is the portal — including the bare
// root, so a tenant who types jmfestates.com never meets the Nexus login.
// Nexus itself is reached on the dashboard's domain.
const ESTATES_HOST = /(^|\.)jmfestates\.com$/i;
const onEstates = () =>
  ESTATES_HOST.test(window.location.hostname) ||
  /^#\/estates(\/|$)/.test(window.location.hash) ||
  /^\/[a-z0-9-]{2,60}\/?$/.test(window.location.pathname);

function Root() {
  const [estates, setEstates] = React.useState(onEstates);
  React.useEffect(() => {
    const sync = () => setEstates(onEstates());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  return (
    <AppAppearance>
      <React.Suspense fallback={null}>
        {estates ? <EstatesPortal /> : <App />}
      </React.Suspense>
    </AppAppearance>
  );
}

applyTheme(readThemePreference());

// Native-only initialisation — no-ops on web/Vercel.
if (Capacitor.isNativePlatform()) {
  StatusBar.setStyle({ style: Style.Dark });
  StatusBar.setBackgroundColor({ color: "#0B1829" });
  // Splash will auto-hide after launchShowDuration (configured in capacitor.config.ts).
  // Call hide() here too so it dismisses as soon as React is ready, whichever is first.
  SplashScreen.hide();
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

reportWebVitals();
